import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// The "⋯" menu that sits in the corner of a card, modelled on Trello's.
// Generic on purpose — it takes a list of items and renders them; the card
// decides what the actions actually do.
//
// An item is { key, label, icon, onSelect, danger?, hidden? }.
//
// The panel is rendered through a portal onto document.body rather than inside
// the card. It has to be: the vendor board nests two scroll containers
// (.vboard scrolls sideways, .vboard-list scrolls down), and an absolutely
// positioned panel inside those gets clipped by both — it stays trapped in the
// column and paints over the card underneath. A portal escapes them, and fixed
// coordinates taken from the dots button keep it pinned to the right card.

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {d}
  </svg>
);

export const MenuIcons = {
  copy: <Icon d={<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>} />,
  link: <Icon d={<><path d="M10 13.5a4 4 0 0 0 5.7.4l3-3a4 4 0 0 0-5.7-5.7l-1.7 1.7" /><path d="M14 10.5a4 4 0 0 0-5.7-.4l-3 3a4 4 0 0 0 5.7 5.7l1.7-1.7" /></>} />,
  share: <Icon d={<><path d="M12 16V4" /><path d="M8 8l4-4 4 4" /><path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" /></>} />,
  eye: <Icon d={<><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="2.6" /></>} />,
  eyeOff: <Icon d={<><path d="M4 4l16 16" /><path d="M10 6a10.6 10.6 0 0 1 2-.2c6.5 0 10 6.2 10 6.2a17 17 0 0 1-3 3.6" /><path d="M6.4 7.6A17 17 0 0 0 2 12s3.5 6.2 10 6.2c1.4 0 2.6-.2 3.7-.6" /></>} />,
  trash: <Icon d={<><path d="M4 7h16" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /><path d="M6 7l1 13h10l1-13" /></>} />,
  move: <Icon d={<><path d="M4 12h15" /><path d="M14 7l5 5-5 5" /></>} />,
  pencil: <Icon d={<><path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z" /><path d="M14.5 6.5l3 3" /></>} />,
  doc: <Icon d={<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></>} />,
  printer: <Icon d={<><path d="M7 9V4h10v5" /><rect x="4" y="9" width="16" height="7" rx="2" /><path d="M7 14h10v6H7z" /></>} />,
};

const GAP = 6;      // between the dots and the panel
const MARGIN = 8;   // smallest gap we'll leave against a screen edge

export default function CardMenu({ items, label = 'Card actions' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const dots = useRef(null);
  const pop = useRef(null);

  const close = useCallback(() => { setOpen(false); setPos(null); }, []);

  // Position once the panel exists and can be measured — that's the only way
  // to know whether it fits below the button or has to flip above it.
  useLayoutEffect(() => {
    if (!open || !dots.current || !pop.current) return;
    const anchor = dots.current.getBoundingClientRect();
    const box = pop.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Right-aligned to the dots, then pulled back inside if it would overhang.
    let left = anchor.right - box.width;
    left = Math.min(Math.max(left, MARGIN), vw - box.width - MARGIN);

    // Below the dots normally; above when there isn't room underneath — which
    // is the common case for a card near the bottom of a phone screen.
    let top = anchor.bottom + GAP;
    if (top + box.height > vh - MARGIN) {
      const above = anchor.top - box.height - GAP;
      top = above >= MARGIN ? above : Math.max(MARGIN, vh - box.height - MARGIN);
    }
    setPos({ top, left });
  }, [open]);

  // Close on an outside tap, Escape, or any scroll/resize. Closing on scroll is
  // deliberate: the panel is fixed to the viewport, so following the card as
  // the board scrolls would mean re-measuring on every frame.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (dots.current?.contains(e.target)) return;
      if (pop.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    // Capture phase so the board's own scroll containers are heard too.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  // Trello dims the board and lifts the card you opened the menu on to the
  // front. Done by tagging the owning card rather than threading an open-state
  // prop through both call sites — the class is the whole effect.
  useEffect(() => {
    const card = dots.current?.closest('.vcard, .lead-card');
    if (!card) return undefined;
    if (open) card.classList.add('card-lifted');
    else card.classList.remove('card-lifted');
    return () => card.classList.remove('card-lifted');
  }, [open]);

  const visible = (items || []).filter(i => i && !i.hidden);

  return (
    <div className="cardmenu">
      <button
        ref={dots}
        className="cardmenu-dots"
        aria-label={label}
        aria-expanded={open}
        // The card underneath opens the vendor on click — every handler here
        // has to stop that from firing too.
        onClick={(e) => { e.stopPropagation(); open ? close() : setOpen(true); }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>

      {open && createPortal(
        <>
        {/* Dims the rest of the board so the card you're acting on is the only
            thing in focus. Tapping it closes the menu. */}
        <div className="cardmenu-scrim" onClick={close} />
        <div
          ref={pop}
          className="cardmenu-pop"
          role="menu"
          // Hidden for the one frame between mounting and being measured, so it
          // never flashes in the top-left corner before it's placed.
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {visible.map(item => (
            <button
              key={item.key}
              role="menuitem"
              className={`cardmenu-item${item.danger ? ' danger' : ''}`}
              onClick={(e) => { e.stopPropagation(); close(); item.onSelect(); }}
            >
              <span className="cardmenu-ico">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        </>,
        document.body
      )}
    </div>
  );
}
